/**
 * Realtime change-signal provider (migration 041 companion).
 *
 * Replaces the dashboard's 3-second polling with Supabase Realtime push.
 * Every studio_id-bearing table has an AFTER-write trigger that broadcasts a
 * tiny `{entity, op}` signal to the per-studio PRIVATE channel
 * `studio:<studio_id>` (event `change`). This provider subscribes ONCE per
 * session, coalesces a burst of signals (debounced), and exposes the set of
 * changed entities + a monotonic `signalSeq` so pages can refetch only what
 * actually changed. `connected` drives the consumer's safety-net (a slow
 * fallback poll + refetch-on-reconnect) so a dropped socket still converges.
 *
 * The signal carries NO row data / PII — just entity + op; the actual refetch
 * still goes through the RLS-enforced read path.
 *
 * Private channels require the socket to carry the user's JWT, so we
 * `supabase.realtime.setAuth(token)` before subscribing AND on every token
 * refresh (`autoRefreshToken` rotates it ~hourly).
 */

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { RealtimeChannel } from "@supabase/supabase-js";
import { supabase } from "./supabase";

export interface RealtimeState {
  /** True while the private channel is SUBSCRIBED. */
  readonly connected: boolean;
  /** Monotonic counter — bumped once per debounced batch of change signals. */
  readonly signalSeq: number;
  /** Entity names changed in the most recent batch. */
  readonly changedEntities: ReadonlyArray<string>;
}

const RealtimeContext = createContext<RealtimeState>({
  connected: false,
  signalSeq: 0,
  changedEntities: [],
});

export function useRealtime(): RealtimeState {
  return useContext(RealtimeContext);
}

const DEBOUNCE_MS = 300;

interface RealtimeProviderProps {
  readonly studioId: string | null;
  readonly children: ReactNode;
}

export function RealtimeProvider({
  studioId,
  children,
}: RealtimeProviderProps): JSX.Element {
  const [state, setState] = useState<RealtimeState>({
    connected: false,
    signalSeq: 0,
    changedEntities: [],
  });
  const pendingRef = useRef<Set<string>>(new Set());
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    if (studioId === null || studioId.length === 0) return undefined;
    let channel: RealtimeChannel | null = null;
    let disposed = false;

    const flush = (): void => {
      timerRef.current = null;
      const entities = Array.from(pendingRef.current);
      pendingRef.current = new Set();
      if (entities.length === 0) return;
      setState((s) => ({
        connected: s.connected,
        signalSeq: s.signalSeq + 1,
        changedEntities: entities,
      }));
    };

    const onChange = (payload: unknown): void => {
      const entity =
        payload !== null &&
        typeof payload === "object" &&
        typeof (payload as { entity?: unknown }).entity === "string"
          ? (payload as { entity: string }).entity
          : null;
      if (entity !== null) pendingRef.current.add(entity);
      if (timerRef.current === null) {
        timerRef.current = window.setTimeout(flush, DEBOUNCE_MS);
      }
    };

    const setup = async (): Promise<void> => {
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      if (token) await supabase.realtime.setAuth(token);
      if (disposed) return;
      channel = supabase.channel(`studio:${studioId}`, {
        config: { private: true },
      });
      channel.on("broadcast", { event: "change" }, (msg) =>
        onChange((msg as { payload?: unknown }).payload),
      );
      channel.subscribe((status) => {
        setState((s) => ({
          ...s,
          connected: status === "SUBSCRIBED",
        }));
      });
    };
    void setup();

    // Keep the realtime socket authenticated across token refreshes.
    const { data: authSub } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (session?.access_token) {
          void supabase.realtime.setAuth(session.access_token);
        }
      },
    );

    return () => {
      disposed = true;
      authSub.subscription.unsubscribe();
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      pendingRef.current = new Set();
      if (channel !== null) void supabase.removeChannel(channel);
    };
  }, [studioId]);

  return (
    <RealtimeContext.Provider value={state}>
      {children}
    </RealtimeContext.Provider>
  );
}
